import React, { useRef, useState } from 'react';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Check from '@mui/icons-material/Check';
import { Layers } from 'lucide-react';
import Responsive from 'src/components/responsive';
import { analyticsClient } from 'src/services/analytics';
import { AnalyticsEvent } from 'src/services/analytics/events';

export enum GroupByOption {
    FRESHNESS = 'freshness',
    PROPERTY = 'property',
}

const GROUP_BY_LABELS: Record<GroupByOption, string> = {
    [GroupByOption.FRESHNESS]: 'Freshness',
    [GroupByOption.PROPERTY]: 'Property',
};

type Props = {
    value: GroupByOption;
    onChange: (value: GroupByOption) => void;
    enabledOptions?: GroupByOption[];
};

export default function GroupBySelector({ value, onChange, enabledOptions }: Props) {
    const buttonRef = useRef<HTMLButtonElement>(null);
    const [open, setOpen] = useState(false);

    return (
        <>
            <Responsive mobile>
                <IconButton
                    ref={buttonRef}
                    onClick={() => setOpen(true)}
                    data-testid="group-by-button"
                >
                    <Layers size={20} />
                </IconButton>
            </Responsive>
            <Responsive desktop>
                <Button
                    ref={buttonRef}
                    color="dark"
                    startIcon={<Layers size={18} />}
                    onClick={() => setOpen(true)}
                    sx={{ whiteSpace: 'nowrap' }}
                    data-testid="group-by-button"
                >
                    <Typography mt={0.25} variant="body2">Group By</Typography>
                </Button>
            </Responsive>

            <Menu
                anchorEl={buttonRef.current}
                open={open}
                onClose={() => setOpen(false)}
                slotProps={{
                    paper: { sx: { width: 200 } },
                }}
            >
                {Object.values(GroupByOption).filter(o => !enabledOptions || enabledOptions.includes(o)).map((option) => (
                    <MenuItem
                        key={option}
                        selected={value === option}
                        onClick={() => {
                            if (option === GroupByOption.PROPERTY) {
                                analyticsClient.track(AnalyticsEvent.CONVERSATIONS_GROUP_BY_PROPERTY_SELECTED);
                            }
                            onChange(option);
                            setOpen(false);
                        }}
                        data-testid={`group-by-option-${option}`}
                    >
                        <ListItemIcon sx={{ visibility: value === option ? 'visible' : 'hidden' }}>
                            <Check fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primaryTypographyProps={{ variant: 'body2' }}>{GROUP_BY_LABELS[option]}</ListItemText>
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
}
